/**
 * GameTransport — the single seam between GameScene and the network.
 *
 * GameScene reaches the outside world at exactly six points: the per-game
 * channel (WS messages in), the REST state fetch, the two gameplay actions
 * (play / pass), and the shared per-session round-transition channel. This
 * interface names those points; createProductionGameTransport() delegates
 * each one to the EXISTING singletons unchanged (same modules, same calls,
 * same timing), so production gameplay behavior is byte-identical.
 *
 * A non-production implementation (the tutorial's scripted transport) may be
 * injected through GameSceneData.transport; when absent, GameScene defaults
 * to the production transport. Nothing else in gameplay may branch on
 * "tutorial vs real" — the seam is the whole difference.
 */
import { GameChannel } from './GameChannel';
import { apiClient } from './ApiClient';
import { acquireSessionChannel, type SessionChannelHandle } from './sessionChannelHub';
import type {
  InitialStateMessage,
  PrivateInitialStateMessage,
  GameUpdateMessage,
  PrivateUpdateMessage,
  GameFinishedMessage,
  ErrorMessage,
  SessionAbandonedMessage,
  RematchStateMessage,
  FullGameStateForUser,
} from '../types/game';

/**
 * The subset of GameChannel that GameScene consumes. GameChannel satisfies
 * this structurally (its fluent registrations return `this`).
 */
export interface GameChannelLike {
  subscribe(): Promise<void>;
  unsubscribe(): void;
  onInitialState(cb: (msg: InitialStateMessage) => void): this;
  onPrivateInitialState(cb: (msg: PrivateInitialStateMessage) => void): this;
  onGameUpdate(cb: (msg: GameUpdateMessage) => void): this;
  onPrivateUpdate(cb: (msg: PrivateUpdateMessage) => void): this;
  onGameFinished(cb: (msg: GameFinishedMessage) => void): this;
  onRematchState(cb: (msg: RematchStateMessage) => void): this;
  onSessionAbandoned(cb: (msg: SessionAbandonedMessage) => void): this;
  onError(cb: (msg: ErrorMessage) => void): this;
  onReconnect(cb: () => void): this;
  onDisconnect(cb: () => void): this;
  onRejected(cb: () => void): this;
}

/** Gesture metadata for a play — informational only. The production
 *  transport ignores it entirely; the tutorial's scripted transport uses it
 *  for the dedicated drag lesson. Derived from the gesture tag Hand already
 *  computes for diagnostics ('click' | 'drag'). */
export interface PlayMeta {
  viaDrag?: boolean;
}

export interface GameTransport {
  createGameChannel(gameId: number, sceneId: number): GameChannelLike;
  getGameState(gameId: number): Promise<FullGameStateForUser>;
  playTile(gameId: number, tileId: number, position: 'left' | 'right', meta?: PlayMeta): Promise<unknown>;
  passTurn(gameId: number): Promise<unknown>;
  acquireSessionChannel(sessionId: number): SessionChannelHandle;
}

/** The default transport: the existing production singletons, unchanged. */
export function createProductionGameTransport(): GameTransport {
  return {
    createGameChannel: (gameId, sceneId) => new GameChannel(gameId, sceneId),
    getGameState:      (gameId) => apiClient.getGameState(gameId),
    // meta is deliberately dropped — the real API takes exactly these three.
    playTile:          (gameId, tileId, position, _meta) => apiClient.playTile(gameId, tileId, position),
    passTurn:          (gameId) => apiClient.passTurn(gameId),
    acquireSessionChannel: (sessionId) => acquireSessionChannel(sessionId),
  };
}

/**
 * Friend-spectating transport (read-only). Deliberately NOT a from-scratch
 * implementation like the tutorial's scripted transport — a spectator is
 * watching REAL data over the REAL channels, just as a non-participant. So
 * this reuses the production GameChannel and session-channel plumbing
 * completely unchanged (the backend alone decides, per-connection identity,
 * whether the subscriber is a participant or a friend-spectator — see
 * GameChannel#subscribed / SessionChannel#subscribed on the backend — so
 * nothing here needs to know or branch on that). Only two of the six seam
 * points actually differ:
 *   - getGameState hits the spectator REST endpoint (hand-blind payload)
 *     instead of the participant one (which 401s for a non-participant).
 *   - playTile/passTurn are hard no-ops that never touch the network. A
 *   spectator's hand is always empty (nothing to drag/click in the first
 *   place), and GameScene is told isSpectator so it never wires input up —
 *   this is the backstop underneath that, not the primary guard.
 */
export function createSpectatorGameTransport(gameId: number): GameTransport {
  void gameId; // part of the shape for symmetry/future use; not needed today
  return {
    createGameChannel: (id, sceneId) => new GameChannel(id, sceneId),
    getGameState:      (id) => apiClient.getSpectatorGameState(id),
    playTile:          () => Promise.reject(new Error('Spectators cannot play')),
    passTurn:          () => Promise.reject(new Error('Spectators cannot play')),
    acquireSessionChannel: (sessionId) => acquireSessionChannel(sessionId),
  };
}
